import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ArrowLeft, Dumbbell, LogOut, MapPin, Megaphone, Search, Shield, Trophy, Users, type LucideIcon } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useProxyConfig, type ProxyStatus } from "@/features/queries";

interface AdminNav {
  to: string;
  label: string;
  Icon: LucideIcon;
  adminOnly?: boolean;
  // Renders a coloured status dot next to the label. The outer
  // component supplies the colour via a small lookup below, so the
  // NAV declaration stays static.
  statusSlot?: "proxy";
}

const NAV: AdminNav[] = [
  // Proxy lives at the top of the menu so the status dot is visible
  // from anywhere in the admin panel — bot health affects everything
  // else, so admins shouldn't have to drill in to notice it's red.
  { to: "/admin/proxy",       label: "Прокси",        Icon: Shield,  adminOnly: true, statusSlot: "proxy" },
  { to: "/admin/trainings",   label: "Тренировки",    Icon: Dumbbell },
  { to: "/admin/tournaments", label: "Турниры",       Icon: Trophy,  adminOnly: true },
  { to: "/admin/spots",       label: "Площадки",      Icon: MapPin,  adminOnly: true },
  { to: "/admin/users",       label: "Пользователи",  Icon: Users,   adminOnly: true },
  { to: "/admin/broadcast",   label: "Рассылка",      Icon: Megaphone, adminOnly: true },
  { to: "/admin/play",        label: "Подбор игр",    Icon: Search,  adminOnly: true },
];

const PROXY_DOT_COLOR: Record<ProxyStatus, string> = {
  ok: "#1E7A5A",
  fail: "#DF3F40",
  checking: "#9AA0A6",
  disabled: "#9AA0A6",
};

export function AdminShell() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Only admins see the proxy item, so skip the fetch for trainers.
  const { data: proxyCfg } = useProxyConfig(!!user?.is_admin);

  const nav = NAV.filter((n) => !n.adminOnly || user?.is_admin);

  return (
    <div className="sp-admin-shell">
      <aside className="sp-admin-sidebar">
        <div className="sp-admin-sidebar-top">
          <button
            onClick={() => navigate("/")}
            className="sp-admin-back"
            title="В приложение"
          >
            <ArrowLeft size={16} />
            <span className="sp-admin-back-label">В приложение</span>
          </button>
          <div className="sp-admin-section-header">
            {user?.is_admin ? "Админка" : "Кабинет тренера"}
          </div>
        </div>

        <nav className="sp-admin-nav">
          {nav.map(({ to, label, Icon, statusSlot }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                "sp-admin-navlink" + (isActive ? " is-active" : "")
              }
            >
              <Icon size={18} strokeWidth={1.75} />
              <span style={{ flex: 1 }}>{label}</span>
              {statusSlot === "proxy" && proxyCfg && (
                <span
                  aria-label={`Статус прокси: ${proxyCfg.status}`}
                  title={`Статус прокси: ${proxyCfg.status}`}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: PROXY_DOT_COLOR[proxyCfg.status],
                    flex: "none",
                  }}
                />
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sp-admin-user">
          <div className="caption" style={{ color: "var(--fg3)" }}>
            {user?.username ?? "admin"}
          </div>
          <button onClick={logout} className="sp-admin-logout">
            <LogOut size={14} /> Выйти
          </button>
        </div>
      </aside>

      <main className="sp-admin-main">
        <Outlet />
      </main>
    </div>
  );
}
